import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { UsersThree, CurrencyEur, MagnifyingGlass, Buildings, Broadcast, UserCircle, CreditCard, Lightning, HourglassMedium, ArrowRight, Funnel, TrendUp, CheckCircle, WarningCircle, Briefcase, ArrowsClockwise, Play } from "@phosphor-icons/react";

const SOURCE_COLOR = { "France Travail": "#00E5FF", "Adzuna": "#A78BFA", "Indeed": "#34D399" };
function srcColor(name) { return SOURCE_COLOR[name] || "#8A8A93"; }

function StripeCatalogCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/stripe/status").then(({ data }) => setSt(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/stripe/setup-catalog");
      toast.success(`Catalogue ${data.mode === "live" ? "LIVE" : "test"} synchronisé (${data.prices?.length || 0} prix)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec setup Stripe"); }
    finally { setBusy(false); }
  };
  if (!st) return null;
  const live = st.mode === "live";
  const fmt = (a, c) => `${(a / 100).toLocaleString("fr-FR")} ${(c || "eur").toUpperCase()}`;
  return (
    <div className="surface rounded-lg p-6 mb-8" data-testid="stripe-catalog-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="overline flex items-center gap-2"><CreditCard size={13} className="text-accent" weight="fill" /> Facturation Stripe</div>
        <span className={`text-xs font-mono border rounded-full px-2 py-0.5 flex items-center gap-1 ${live ? "text-accent border-accent/40" : "text-amber-400 border-amber-500/30"}`} data-testid="stripe-mode">
          {live ? <CheckCircle size={12} weight="fill" /> : <WarningCircle size={12} weight="fill" />} {live ? "LIVE (production)" : "TEST (sandbox)"}
        </span>
      </div>
      <p className="text-xs text-muted mb-4">Compte : <span className="font-mono">{st.account || "—"}</span>. En production, définissez <span className="font-mono text-fg/80">STRIPE_SECRET_KEY_LIVE</span> &amp; <span className="font-mono text-fg/80">STRIPE_WEBHOOK_SECRET_LIVE</span> dans les variables de déploiement, puis cliquez ci-dessous pour créer le catalogue interKO sur le compte Talent Junction.</p>
      {st.error && <div className="text-xs text-destructive mb-3">{st.error}</div>}
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        {["interko_unique_29_monthly", "interko_standard_365", "interko_monthly"].map((lk) => {
          const p = (st.prices || []).find((x) => x.lookup_key === lk);
          const label = { interko_unique_29_monthly: "Offre unique (mensuel)", interko_standard_365: "Annuel 365 € (historique)", interko_monthly: "Mensuel (historique)" }[lk];
          return (
            <div key={lk} className="bg-surface-2 border border-border rounded-md p-3" data-testid={`stripe-price-${lk}`}>
              <div className="text-xs text-muted">{label}</div>
              <div className="font-mono text-lg font-bold text-fg">{p ? fmt(p.amount, p.currency) : "—"}</div>
              <div className="text-[10px] font-mono text-muted">{p ? (p.livemode ? "live" : "test") : "absent"}</div>
            </div>
          );
        })}
      </div>
      <button onClick={setup} disabled={busy} data-testid="stripe-setup-catalog"
        className="flex items-center gap-2 bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50 text-sm">
        <CheckCircle size={15} weight="fill" className={busy ? "animate-pulse" : ""} /> {busy ? "Synchronisation…" : "Créer / vérifier le catalogue"}
      </button>
    </div>
  );
}

function ProviderCol({ title, Icon, color, m, note }) {
  return (
    <div className="bg-surface-2 border border-border rounded-md p-4" data-testid={`jobs-cmp-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} weight="duotone" style={{ color }} />
        <span className="text-sm font-semibold text-fg">{title}</span>
        {note && <span className="text-[10px] font-mono text-amber-400 border border-amber-500/30 rounded-full px-1.5 py-0.5">{note}</span>}
      </div>
      <div className="flex items-end gap-2">
        <div className="font-display text-3xl font-extrabold" style={{ color }} data-testid={`jobs-cmp-${title.replace(/\s+/g, "-").toLowerCase()}-created`}>{m.created}</div>
        <div className="text-xs text-muted mb-1.5">nouveaux signaux</div>
      </div>
      <div className="text-[11px] text-muted font-mono mt-2 space-y-0.5">
        <div>{m.received} offres reçues · unicité {m.unique_yield}%</div>
        <div>{m.updated} enrichis · {m.cross_source_merges} cross-source</div>
        <div>{m.runs} collecte{m.runs > 1 ? "s" : ""} · dernière : {m.last_run ? new Date(m.last_run).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "—"}</div>
      </div>
    </div>
  );
}

function JobsComparisonCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = (d) => api.get(`/admin/collection/jobs-comparison?days=${d}`).then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(days); }, [days]);

  const collect = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/collection/brightdata/collect", { it_france: true });
      if (r.error) toast.error(r.error);
      else toast.success(`Collecte IT · France lancée${r.simulate ? " · mode simulé (0 crédit)" : ""}`);
      setTimeout(() => load(days), 6000);
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de la collecte"); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const bd = data.brightdata, gj = data.google_jobs;
  const bdNote = bd.simulate_runs > 0 && bd.created === 0 ? "mode simulé" : null;
  const winner = bd.created === gj.created ? null : (bd.created > gj.created ? "Bright Data" : "Google Jobs");

  return (
    <div className="surface rounded-lg p-6 mb-8" data-testid="jobs-comparison-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="overline flex items-center gap-2"><Briefcase size={13} className="text-accent" weight="fill" /> Emplois : Bright Data vs Google Jobs</div>
        <div className="flex items-center gap-2">
          <div className="flex bg-surface-2 border border-border rounded-md overflow-hidden" data-testid="jobs-cmp-period">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`jobs-cmp-period-${d}`}
                className={`px-2.5 py-1 text-xs font-mono ${days === d ? "bg-accent text-black font-bold" : "text-muted hover:text-fg"}`}>{d}j</button>
            ))}
          </div>
          <button onClick={() => load(days)} title="Rafraîchir" className="p-1.5 rounded-md text-muted hover:text-accent"><ArrowsClockwise size={14} /></button>
          <button onClick={collect} disabled={busy} data-testid="jobs-cmp-collect"
            className="flex items-center gap-1.5 bg-accent text-black text-xs font-semibold px-3 py-1.5 rounded-md hover-lift disabled:opacity-50">
            <Play size={13} weight="fill" className={busy ? "animate-pulse" : ""} /> {busy ? "Lancement…" : "Collecter (IT · France)"}
          </button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <ProviderCol title="Bright Data" Icon={Briefcase} color="#00E5FF" m={bd} note={bdNote} />
        <ProviderCol title="Google Jobs" Icon={MagnifyingGlass} color="#34D399" m={gj} />
      </div>
      <div className="text-[11px] text-muted mt-3" data-testid="jobs-cmp-verdict">
        {winner
          ? <>Sur {data.days} jours, <b className="text-fg/80">{winner}</b> a ramené le plus de nouveaux signaux ({Math.max(bd.created, gj.created)} vs {Math.min(bd.created, gj.created)}).</>
          : <>Sur {data.days} jours, les deux sources sont à égalité ({bd.created} nouveaux signaux chacune).</>}
        {bdNote && " Bright Data est en mode simulé en preview (aucune ingestion) — les chiffres réels apparaîtront après une collecte en production."}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="surface rounded-lg p-5 hover-lift" data-testid={`stat-${label}`}>
      <div className={`w-10 h-10 rounded-md flex items-center justify-center mb-3 ${accent ? "bg-accent/12 border border-accent/25" : "bg-surface-2 border border-border"}`}>
        <Icon size={20} weight="duotone" className={accent ? "text-accent" : "text-muted"} />
      </div>
      <div className="font-display text-3xl font-extrabold text-fg">{value}</div>
      <div className="text-sm text-muted mt-0.5">{label}</div>
      {sub && <div className="text-xs text-muted font-mono mt-2">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/funnel").then(({ data }) => setFunnel(data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>;

  const s = stats.subscriptions;

  return (
    <div data-testid="admin-dashboard">
      <div className="overline mb-2">Vue d'ensemble</div>
      <h1 className="font-display text-3xl font-bold mb-8">Tableau de bord</h1>

      {stats.pending_review > 0 && (
        <Link to="/admin/organizations" data-testid="pending-review-banner"
          className="flex items-center justify-between gap-4 bg-amber-500/10 border border-amber-500/30 rounded-lg p-5 mb-4 hover-lift">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <HourglassMedium size={20} weight="duotone" className="text-amber-400" />
            </div>
            <div>
              <div className="font-display font-bold text-fg">{stats.pending_review} organisation{stats.pending_review > 1 ? "s" : ""} en attente de validation</div>
              <div className="text-sm text-muted">Validez les nouvelles organisations en attente.</div>
            </div>
          </div>
          <span className="flex items-center gap-1 text-sm text-amber-400 font-semibold shrink-0">Traiter <ArrowRight size={15} /></span>
        </Link>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <Stat icon={UsersThree} label="Utilisateurs" value={stats.users_total} sub={`${s.active} actifs · ${s.canceled} résiliés`} accent />
        <Stat icon={CurrencyEur} label="Revenus (payés)" value={`${stats.revenue_eur} €`} sub={`${stats.paid_transactions} transactions`} accent />
        <Stat icon={MagnifyingGlass} label="Recherches lancées" value={stats.searches_total} sub={`${stats.saved_searches_total} sauvegardées`} />
        <Stat icon={CreditCard} label="Abonnements actifs" value={s.active} sub={`${stats.plans.standard || 0} offre unique · ${stats.plans.monthly}M · ${stats.plans.annual}A · ${stats.plans.lifetime}L`} />
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Stat icon={Buildings} label="Entreprises" value={stats.companies} />
        <Stat icon={Broadcast} label="Signaux marché" value={stats.signals} />
        <Stat icon={UserCircle} label="Décideurs / contacts" value={stats.contacts} />
      </div>

      {/* Offres IT collectées aujourd'hui, par source (admin uniquement) */}
      <div className="surface rounded-lg p-6 mb-8" data-testid="collection-kpi">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="overline flex items-center gap-2"><TrendUp size={13} className="text-accent" weight="fill" /> Offres IT collectées aujourd'hui</div>
          <Link to="/admin/sources" className="text-xs text-accent hover:underline flex items-center gap-1">Gérer les sources <ArrowRight size={13} /></Link>
        </div>
        <div className="flex items-end gap-3 mb-4">
          <div className="font-display text-4xl font-extrabold text-fg" data-testid="kpi-today-total">{stats.signals_today_total ?? 0}</div>
          <div className="text-sm text-muted mb-1.5">nouvelle{(stats.signals_today_total ?? 0) > 1 ? "s" : ""} offre{(stats.signals_today_total ?? 0) > 1 ? "s" : ""} aujourd'hui</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {["France Travail", "Adzuna", "Indeed"].map((src) => {
            const today = (stats.signals_today_by_source || {})[src] || 0;
            const total = (stats.signals_by_source || {})[src] || 0;
            return (
              <div key={src} className="bg-surface-2 border border-border rounded-md p-4" data-testid={`kpi-source-${src.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: srcColor(src) }} />
                  <span className="text-xs text-muted">{src === "Indeed" ? "Indeed (HasData)" : src}</span>
                </div>
                <div className="font-mono text-2xl font-bold" style={{ color: srcColor(src) }}>+{today}</div>
                <div className="text-[11px] text-muted font-mono mt-1">{total} au total</div>
              </div>
            );
          })}
        </div>
      </div>

      <StripeCatalogCard />

      <JobsComparisonCard />

      <div className="surface rounded-lg p-6">
        <div className="overline mb-4 flex items-center gap-2"><Lightning size={13} className="text-accent" weight="fill" /> Répartition des abonnements</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { k: "Offre unique", v: stats.plans.standard || 0, c: "#00E5FF" },
            { k: "Actifs", v: s.active, c: "#4F46E5" },
            { k: "Annulés", v: s.canceled, c: "#EF4444" },
            { k: "Sans abo", v: s.none, c: "#8A8A93" },
          ].map((x) => (
            <div key={x.k} className="bg-surface-2 border border-border rounded-md p-4">
              <div className="font-mono text-2xl font-bold" style={{ color: x.c }}>{x.v}</div>
              <div className="text-xs text-muted mt-1">{x.k}</div>
            </div>
          ))}
        </div>
      </div>

      {funnel && (
        <div className="surface rounded-lg p-6 mt-4" data-testid="conversion-funnel">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="overline flex items-center gap-2"><Funnel size={13} className="text-accent" weight="fill" /> Entonnoir de conversion</div>
            <span className="text-xs font-mono text-muted">Conversion globale : <span className="text-accent font-bold">{funnel.overall_conversion}%</span> · {funnel.total_events} événements</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {funnel.stages.map((st, i) => {
              const max = funnel.stages[0].count || 1;
              const pct = Math.round((st.count / max) * 100);
              return (
                <div key={st.key} className="bg-surface-2 border border-border rounded-md p-4" data-testid={`funnel-${st.key}`}>
                  <div className="flex items-center justify-between text-xs text-muted mb-1">
                    <span>Étape {i + 1}</span>
                    {st.conv_from_prev != null && <span className="font-mono text-accent">{st.conv_from_prev}%</span>}
                  </div>
                  <div className="font-mono text-2xl font-bold text-fg">{st.count}</div>
                  <div className="text-xs text-muted mt-0.5">{st.label}</div>
                  <div className="h-1 rounded-full bg-bg mt-2 overflow-hidden">
                    <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
